// Email validation utility functions

/**
 * Validates and normalizes an email address
 * @param {string} email - The email address to validate
 * @returns {{ isValid: boolean, normalizedEmail: string, error: string | null }}
 */
export const validateEmail = (email) => {
  if (!email) {
    return {
      isValid: false,
      normalizedEmail: "",
      error: "Email is required",
    };
  }

  // Remove leading/trailing whitespace
  const normalizedEmail = email.trim().toLowerCase();

  // Basic format validation
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(normalizedEmail)) {
    return {
      isValid: false,
      normalizedEmail,
      error: "Please enter a valid email address",
    };
  }

  // Additional validation for common typos
  const commonDomains = {
    gmial: "gmail",
    gmal: "gmail",
    gamil: "gmail",
    gnail: "gmail",
    hotmal: "hotmail",
    hotmial: "hotmail",
    yaho: "yahoo",
    yahooo: "yahoo",
  };

  const [localPart, domain] = normalizedEmail.split("@");
  const [domainName] = domain.split(".");

  if (commonDomains[domainName]) {
    return {
      isValid: false,
      normalizedEmail,
      error: `Did you mean ${localPart}@${commonDomains[domainName]}.com?`,
    };
  }

  return {
    isValid: true,
    normalizedEmail,
    error: null,
  };
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} - The debounced function
 */
export const debounce = (func, wait) => {
  let timeoutId;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeoutId);
      func(...args);
    };

    clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
  };
};
